// Debug surface for agent-driven inspection and testing.
//
// Installs `window.__gx` with structured access to the scene graph, the world
// store, and the build tools, so work on this app can be set up and checked
// from code instead of by clicking at guessed coordinates and reading the
// result off a screenshot. Two things it buys: reproducibility, because a fixed
// camera and a fixed stroke seed make a before/after comparison the same view
// twice rather than two guesses; and the ability to check the actual state,
// which is how you catch "1473 instances, all at the origin" in one call rather
// than from a picture and a hunch.
//
// This deliberately does NOT report renderer statistics. An earlier version
// tried, got it wrong twice, and never got it right: three resets its counters
// at the start of every render and this app renders more than once per frame,
// and the component turned out not to be mounting where the source said it was,
// so a scene visibly drawing thousands of instances reported zero draw calls. A
// tool that confidently returns a wrong number is worse than one that does not
// offer the number at all — and desktop draw calls were never going to answer
// the question that actually matters here, which is overdraw on a Quest. That
// needs the headset regardless.
//
// DEV ONLY. `import.meta.env.DEV` is a compile-time constant, so this module is
// dropped from production bundles rather than shipped and hidden — an API that
// dumps store state and drives the builder is a backdoor, not a feature flag.

import { useRef, useLayoutEffect } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useWorldStore } from '../stores/worldStore';

interface NodeInfo {
  name: string;
  type: string;
  visible: boolean;
  position: [number, number, number];
  /** Present for InstancedMesh — the thing worth knowing about a foliage layer. */
  instances?: number;
  material?: string;
  children?: NodeInfo[];
}

function describe(o: THREE.Object3D, depth: number): NodeInfo {
  const mesh = o as THREE.Mesh & { count?: number; isInstancedMesh?: boolean };
  const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
  const info: NodeInfo = {
    name: o.name || '(unnamed)',
    type: o.type,
    visible: o.visible,
    position: [
      +o.position.x.toFixed(3),
      +o.position.y.toFixed(3),
      +o.position.z.toFixed(3),
    ],
  };
  if (mesh.isInstancedMesh) info.instances = mesh.count;
  if (mat) {
    info.material = Array.isArray(mat)
      ? mat.map((m) => m.type).join(',')
      : `${mat.type}${(mat as any).transparent ? ' (transparent)' : ''}`;
  }
  if (depth > 0 && o.children.length) {
    info.children = o.children.map((c) => describe(c, depth - 1));
  }
  return info;
}

// The component is mounted inside more than one Canvas (desktop and mobile), so
// instances register themselves here and lookups pick the one with the most
// populated scene. Guessing which canvas was "the real one" from render
// activity was unreliable in practice.
interface Ctx { scene: THREE.Scene; camera: THREE.Camera; controls: any }
const registry: Ctx[] = [];

function active(): Ctx | null {
  if (!registry.length) return null;
  let best = registry[0];
  for (const c of registry) {
    if ((c.scene?.children?.length ?? 0) > (best.scene?.children?.length ?? 0)) best = c;
  }
  return best;
}

export const DebugInspector: React.FC = () => {
  const { scene, camera, controls } = useThree();
  const self = useRef<Ctx>({ scene, camera, controls });

  self.current.scene = scene;
  self.current.camera = camera;
  self.current.controls = controls;

  useLayoutEffect(() => {
    const c = self.current;
    registry.push(c);
    return () => {
      const i = registry.indexOf(c);
      if (i >= 0) registry.splice(i, 1);
    };
  }, []);

  return null;
};

if (import.meta.env.DEV) {
  const api = {
    /** Scene hierarchy to `depth` levels. */
    graph(depth = 3) {
      const ctx = active();
      return ctx ? describe(ctx.scene, depth) : { error: 'no canvas registered' };
    },

    /** Find nodes by substring of name, with their world positions. */
    find(needle: string) {
      const ctx = active();
      const out: any[] = [];
      if (!ctx) return out;
      const p = new THREE.Vector3();
      ctx.scene.traverse((o: any) => {
        if (!o.name || !o.name.toLowerCase().includes(needle.toLowerCase())) return;
        o.getWorldPosition(p);
        out.push({
          name: o.name,
          type: o.type,
          visible: o.visible,
          world: [+p.x.toFixed(3), +p.y.toFixed(3), +p.z.toFixed(3)],
          instances: o.count,
        });
      });
      return out;
    },

    /** Instanced meshes and their instance counts — the foliage sanity check. */
    instances() {
      const ctx = active();
      if (!ctx) return { error: 'no canvas registered' };
      const out: any[] = [];
      ctx.scene.traverse((o: any) => {
        if (o.isInstancedMesh) {
          out.push({ name: o.name || '(unnamed)', count: o.count, visible: o.visible });
        }
      });
      return { meshes: out.length, total: out.reduce((a, b) => a + b.count, 0), detail: out };
    },

    /** The parts of world state worth asserting against. */
    store() {
      const s = useWorldStore.getState();
      return {
        buildTool: s.buildTool,
        ground: s.groundSettings,
        groundVisible: s.groundVisible,
        primitives: Object.keys(s.primitives).length,
        lights: Object.keys(s.lights).length,
        contentItems: Object.keys(s.contentItems).length,
        foliageBrush: s.foliageBrush,
        foliageStrokes: s.foliageStrokes.map((k) => ({
          id: k.id,
          points: k.points.length,
          radius: k.radius,
          density: k.density,
          trees: !!k.trees,
          seed: k.seed,
        })),
      };
    },

    /** Arm a build tool without hunting for its toolbar pixel. */
    tool(name: string) {
      useWorldStore.getState().setBuildTool(name as any);
      return useWorldStore.getState().buildTool;
    },

    brush(patch: Record<string, any>) {
      useWorldStore.getState().setFoliageBrush(patch as any);
      return useWorldStore.getState().foliageBrush;
    },

    ground(patch: Record<string, any>) {
      useWorldStore.getState().setGroundSettings(patch as any);
      return useWorldStore.getState().groundSettings;
    },

    /** Place the camera deterministically so two screenshots are comparable. */
    camera(pos: [number, number, number], target: [number, number, number] = [0, 0, 0]) {
      const ctx = active();
      if (!ctx) return { error: 'no canvas registered' };
      const cam = ctx.camera as THREE.PerspectiveCamera;
      cam.position.set(...pos);
      const c: any = ctx.controls;
      if (c?.target) {
        c.target.set(...target);
        c.update?.();
      } else {
        cam.lookAt(new THREE.Vector3(...target));
      }
      cam.updateProjectionMatrix();
      return { position: pos, target };
    },

    /** Paint a stroke without a pointer, for reproducible foliage tests. */
    paint(points: [number, number][], patch: Record<string, any> = {}) {
      const s = useWorldStore.getState();
      const b = { ...s.foliageBrush, ...patch };
      s.addFoliageStroke({
        id: `dbg-${Date.now()}`,
        points,
        radius: b.radius,
        density: b.trees ? b.density * 0.045 : b.density,
        species: b.trees ? [1] : b.species,
        scaleMin: b.trees ? 0.85 : 0.75,
        scaleMax: b.trees ? 1.5 : 1.35,
        y: 0,
        seed: 12345, // fixed, so a test scene is byte-identical across runs
        trees: !!b.trees,
        erase: !!b.erase,
      });
      return useWorldStore.getState().foliageStrokes.length;
    },

    clearFoliage() {
      useWorldStore.getState().clearFoliageStrokes();
      return 0;
    },
  };
  (window as any).__gx = api;
}

export default DebugInspector;
